import {
    useImperativeHandle,
    useLayoutEffect,
    useEffect,
    useState,
    useMemo,
    useRef,
    useId
} from "react";
import {
    type LayoutChangeEvent,
    useWindowDimensions,
    Animated,
    View
} from "react-native";
import type IDateTimeSheetProps from "./type";
import stylesheet, {
    DATE_TIME_SHEET_CALENDAR_TYPE_KEYS,
    DATE_TIME_SHEET_CALENDAR_TYPES,
    getDateTimeSheetPopoverWidth,
    getDateTimeSheetTitleKey,
    getDateTimeSheetSummary,
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../core/hooks";
import {
    useMotionTiming
} from "../../helpers/motion";
import {
    usePointerType
} from "../../helpers/pointer";
import type {
    IBottomSheetRef
} from "../bottomSheet/type";
import type {
    IDateSelectorRef
} from "../dateSelector/type";
import type {
    DateCalendarType
} from "../dateTimePicker/type";
import type {
    IMonthSelectorRef
} from "../monthSelector/type";
import type {
    PopoverContextType
} from "../popover/type";
import type {
    IYearSelectorRef
} from "../yearSelector/type";
import {
    PopoverContext
} from "../popover/context";
import {
    warnOnce
} from "../../utils/development";
import MonthSelector from "../monthSelector";
import DateSelector from "../dateSelector";
import TimeSelector from "../timeSelector";
import YearSelector from "../yearSelector";
import BottomSheet from "../bottomSheet";
import Loading from "../loading";
import Popover from "../popover";
import Button from "../button";
import Tabs from "../tabs";

const DateTimeSheet = ({
    isTimeSelectWorksWithCenterWhenTimePicker,
    dateCalendarType: dateCalendarTypeProp,
    multipleSelectMinimumRequiredDayCount,
    localeBasedFirstDayOfWeek = true,
    isWorkWith24HoursFormat = true,
    multipleSelectMinimumRequireMS,
    isShowTodayIndicator = true,
    dayOfWeekLength = "short",
    isShowCalendarChangerTool,
    isShowCalenderChangerTool,
    isWorkWithSeconds = false,
    isShowWeekNumber = false,
    LoadingIconComponentProp,
    onDateCalendarTypeChange,
    captionLayout = "label",
    multipleSelectMaximumMS,
    multipleSelectDayLimit,
    monthOfYearLengthType,
    selectMultipleObject,
    isOpen: isOpenProp,
    isShowTools = true,
    isWorkWithRealtime,
    dateTimePickerKey,
    bottomSheetProps,
    bottomSheetRef,
    customLocalize,
    monthCount = 1,
    onOpenChange,
    selectObject,
    customTheme,
    description,
    setIsActive,
    pickerType,
    anchorRef,
    dateRange,
    isLoading,
    maxChoice,
    minChoice,
    isActive,
    maxDate,
    minDate,
    variant,
    cancel,
    clean,
    clear,
    title,
    date,
    ok
}: IDateTimeSheetProps) => {
    const {
        controlHeights,
        shapeConfigs,
        iconSizes,
        borders,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        width: windowWidth
    } = useWindowDimensions();

    const {
        activeLocale,
        localize
    } = NCoreUIKitLocalize.useContext(customLocalize);

    const pointerType = usePointerType();

    const popoverId = useId();

    const isCurrentShowCalendarChangerTool = isShowCalendarChangerTool ?? isShowCalenderChangerTool ?? true;

    const isPopover = pointerType === "fine" && !!anchorRef;

    const isPopoverRef = useRef(isPopover);

    isPopoverRef.current = isPopover;

    const [
        isSheetContentReady,
        setIsSheetContentReady
    ] = useState(false);

    const [
        dateCalendarType,
        setDateCalendarType
    ] = useState<DateCalendarType>(dateCalendarTypeProp ?? "day");

    const monthCalendarRef = useRef<IMonthSelectorRef>(null);

    const yearCalendarRef = useRef<IYearSelectorRef>(null);

    const dayCalendarRef = useRef<IDateSelectorRef>(null);

    const bottomSheetInnerRef = useRef<IBottomSheetRef>(null);

    const isOpen = isOpenProp ?? isActive ?? false;

    const isOwnerClosingRef = useRef(false);

    const calendarHeightRef = useRef<number | undefined>(undefined);

    const isCalendarHeightPendingRef = useRef(false);

    const calendarHeight = useRef(new Animated.Value(0)).current;

    const [
        isCalendarHeightAnimating,
        setIsCalendarHeightAnimating
    ] = useState(false);

    const {
        duration: calendarHeightDuration,
        easing: calendarHeightEasing
    } = useMotionTiming("contentSwap", customTheme);

    const currentDateCalendarType = dateCalendarTypeProp ?? dateCalendarType;

    const isDayGridShown = pickerType !== "time" && currentDateCalendarType === "day";

    const twoMonthsPopoverWidth = getDateTimeSheetPopoverWidth({
        isShowWeekNumber: isDayGridShown && isShowWeekNumber,
        controlHeights,
        monthCount: 2,
        shapeConfigs,
        borders,
        spaces
    });

    const isTwoMonths = isPopover && monthCount === 2 && isDayGridShown && windowWidth >= twoMonthsPopoverWidth + spaces.spacingMd * 2;

    const popoverWidth = isTwoMonths ? twoMonthsPopoverWidth : getDateTimeSheetPopoverWidth({
        isShowWeekNumber: isDayGridShown && isShowWeekNumber,
        controlHeights,
        monthCount: 1,
        shapeConfigs,
        borders,
        spaces
    });

    const summary = getDateTimeSheetSummary({
        isWorkWith24HoursFormat,
        isWorkWithSeconds,
        activeLocale,
        pickerType,
        dateRange,
        localize,
        variant,
        date
    });

    const titleKey = getDateTimeSheetTitleKey({
        pickerType,
        variant
    });

    const {
        contentContainer: contentContainerDynamicStyle,
        loadingContainer: loadingContainerDynamicStyle,
        footerContainer: footerContainerDynamicStyle
    } = useStyles({
        spaces
    });

    useEffect(() => {
        if(isActive === undefined) {
            return;
        }

        warnOnce({
            message: "DateTimeSheet: \"isActive\" is deprecated and will be removed in the next major version. Use \"isOpen\" instead."
        });
    }, [
        isActive
    ]);

    useEffect(() => {
        if(clean === undefined) {
            return;
        }

        warnOnce({
            message: "DateTimeSheet: \"clean\" is deprecated and will be removed in the next major version. Use \"clear\" instead."
        });
    }, [
        clean
    ]);

    useEffect(() => {
        if(isShowCalenderChangerTool === undefined) {
            return;
        }

        warnOnce({
            message: "DateTimeSheet: \"isShowCalenderChangerTool\" is deprecated and will be removed in the next major version. Use \"isShowCalendarChangerTool\" instead."
        });
    }, [
        isShowCalenderChangerTool
    ]);

    useEffect(() => {
        if(variant !== "rrule") {
            return;
        }

        warnOnce({
            message: "DateTimeSheet: variant \"rrule\" currently has no effect. The sheet has no recurrence editor, so the calendar selects nothing and \"selectDateRule\" and \"dateRule\" are never used."
        });
    }, [
        variant
    ]);

    useEffect(() => {
        if(maxChoice === undefined || maxChoice === -1) {
            return;
        }

        warnOnce({
            message: "DateTimeSheet: \"maxChoice\" currently has no effect. A sheet picks one value or one range."
        });
    }, [
        maxChoice
    ]);

    useEffect(() => {
        if(isLoading) {
            setIsSheetContentReady(true);
        }
    }, [
        isLoading
    ]);

    const handleOwnerClose: IBottomSheetRef["close"] = (onClosedCallback) => {
        isOwnerClosingRef.current = true;

        if(isPopoverRef.current) {
            setIsActive(false);

            onClosedCallback?.({
                id: dateTimePickerKey
            });
        } else {
            bottomSheetInnerRef.current?.close(onClosedCallback);
        }

        isOwnerClosingRef.current = false;
    };

    useImperativeHandle(
        bottomSheetRef,
        () => ({
            setIsContentReady: (status) => {
                bottomSheetInnerRef.current?.setIsContentReady(status);
            },
            open: () => {
                bottomSheetInnerRef.current?.open();
            },
            close: handleOwnerClose
        }),
        []
    );

    const onCalendarTypeChange = (calendarType: string) => {
        const nextCalendarType = DATE_TIME_SHEET_CALENDAR_TYPES.find((type) => type === calendarType);

        if(!nextCalendarType) {
            return;
        }

        setDateCalendarType(nextCalendarType);

        onDateCalendarTypeChange?.(nextCalendarType);
    };

    const onClearPress = () => {
        (clear ?? clean)?.();
    };

    const onTodayPress = () => {
        const today = new Date();

        monthCalendarRef.current?.changeCurrentYear(today);

        yearCalendarRef.current?.changeCurrentView(today);

        dayCalendarRef.current?.changeCurrentMonth(today);
    };

    const onOkPress = () => {
        if(ok() === false) {
            return;
        }

        handleOwnerClose();
    };

    const handlePopoverOpen = () => {
        onOpenChange?.(true);

        setIsActive(true);
    };

    const handlePopoverClose = () => {
        if(!isOpen) {
            return;
        }

        onOpenChange?.(false);

        if(isWorkWithRealtime) {
            setIsActive(false);

            return;
        }

        cancel();
    };

    const popoverActionsRef = useRef({
        handleClose: handlePopoverClose,
        handleOpen: handlePopoverOpen
    });

    popoverActionsRef.current = {
        handleClose: handlePopoverClose,
        handleOpen: handlePopoverOpen
    };

    const popoverContextValue = useMemo((): PopoverContextType | undefined => {
        if(!anchorRef) {
            return undefined;
        }

        return {
            handleToggle: () => isOpen ? popoverActionsRef.current.handleClose() : popoverActionsRef.current.handleOpen(),
            handleClose: () => popoverActionsRef.current.handleClose(),
            handleOpen: () => popoverActionsRef.current.handleOpen(),
            contentId: `${popoverId}-content`,
            setIsAnchored: () => undefined,
            isMatchAnchorWidth: false,
            touchBehaviour: "popover",
            isAvoidCollisions: true,
            triggerRef: anchorRef,
            isAnchored: false,
            align: "start",
            customLocalize,
            isModal: false,
            side: "bottom",
            size: "medium",
            customTheme,
            anchorRef,
            isOpen
        };
    }, [
        customLocalize,
        customTheme,
        popoverId,
        anchorRef,
        isOpen
    ]);

    useLayoutEffect(() => {
        if(calendarHeightRef.current === undefined) {
            return;
        }

        calendarHeight.setValue(calendarHeightRef.current);

        isCalendarHeightPendingRef.current = true;

        setIsCalendarHeightAnimating(true);
    }, [
        currentDateCalendarType
    ]);

    const onCalendarLayout = (event: LayoutChangeEvent) => {
        const nextHeight = event.nativeEvent.layout.height;

        const isPending = isCalendarHeightPendingRef.current;

        isCalendarHeightPendingRef.current = false;

        calendarHeightRef.current = nextHeight;

        if(!isPending) {
            return;
        }

        Animated.timing(calendarHeight, {
            duration: calendarHeightDuration,
            easing: calendarHeightEasing,
            useNativeDriver: false,
            toValue: nextHeight
        }).start(({
            finished
        }) => {
            if(finished) {
                setIsCalendarHeightAnimating(false);
            }
        });
    };

    const handleSheetOpenChange = (isNextOpen: boolean) => {
        if(isNextOpen || !isOpen || isOwnerClosingRef.current) {
            return;
        }

        onOpenChange?.(false);

        if(isWorkWithRealtime) {
            return;
        }

        cancel();
    };

    const onClosed = (props: {
        id: string;
    }) => {
        setIsActive(false);

        bottomSheetProps?.onClosed?.(props);
    };

    const renderCalendarChangerTrigger = (calendarType: DateCalendarType) => {
        return <Tabs.Trigger
            title={localize(DATE_TIME_SHEET_CALENDAR_TYPE_KEYS[calendarType])}
            key={`calendar-type-${calendarType}`}
            customLocalize={customLocalize}
            customTheme={customTheme}
            value={calendarType}
        />;
    };

    const renderCalendarChanger = () => {
        if(!isCurrentShowCalendarChangerTool) {
            return null;
        }

        if(pickerType === "time" || dateCalendarTypeProp || isPopover) {
            return null;
        }

        return <Tabs
            onValueChange={(calendarType) => onCalendarTypeChange(calendarType)}
            value={currentDateCalendarType}
            customLocalize={customLocalize}
            spreadBehaviour="stretch"
            customTheme={customTheme}
            listRole="radiogroup"
            variant="segment"
            size="medium"
        >
            <Tabs.List
                title={localize("date-time-sheet-calendar-view")}
                customTheme={customTheme}
            >
                {DATE_TIME_SHEET_CALENDAR_TYPES.map((calendarType) => renderCalendarChangerTrigger(calendarType))}
            </Tabs.List>
        </Tabs>;
    };

    const renderCalendar = () => {
        if(pickerType === "time") {
            return null;
        }

        if(currentDateCalendarType === "month") {
            return <MonthSelector
                multipleSelectMinimumRequiredDayCount={multipleSelectMinimumRequiredDayCount}
                setIsSheetContentReady={setIsSheetContentReady}
                multipleSelectDayLimit={multipleSelectDayLimit}
                monthOfYearLengthType={monthOfYearLengthType}
                selectMultipleObject={selectMultipleObject}
                isShowTodayIndicator={isShowTodayIndicator}
                customLocalize={customLocalize}
                selectObject={selectObject}
                customTheme={customTheme}
                ref={monthCalendarRef}
                dateRange={dateRange}
                isShowCard={false}
                maxDate={maxDate}
                minDate={minDate}
                variant={variant}
                date={date}
            />;
        }

        if(currentDateCalendarType === "year") {
            return <YearSelector
                multipleSelectMinimumRequiredDayCount={multipleSelectMinimumRequiredDayCount}
                setIsSheetContentReady={setIsSheetContentReady}
                multipleSelectDayLimit={multipleSelectDayLimit}
                selectMultipleObject={selectMultipleObject}
                isShowTodayIndicator={isShowTodayIndicator}
                customLocalize={customLocalize}
                selectObject={selectObject}
                customTheme={customTheme}
                ref={yearCalendarRef}
                dateRange={dateRange}
                isShowCard={false}
                maxDate={maxDate}
                minDate={minDate}
                variant={variant}
                date={date}
            />;
        }

        return <DateSelector
            multipleSelectMinimumRequiredDayCount={multipleSelectMinimumRequiredDayCount}
            localeBasedFirstDayOfWeek={localeBasedFirstDayOfWeek}
            setIsSheetContentReady={setIsSheetContentReady}
            multipleSelectDayLimit={multipleSelectDayLimit}
            selectMultipleObject={selectMultipleObject}
            isShowTodayIndicator={isShowTodayIndicator}
            isShowWeekNumber={isShowWeekNumber}
            dayOfWeekLength={dayOfWeekLength}
            monthCount={isTwoMonths ? 2 : 1}
            customLocalize={customLocalize}
            captionLayout={captionLayout}
            selectObject={selectObject}
            customTheme={customTheme}
            dateRange={dateRange}
            ref={dayCalendarRef}
            isShowCard={false}
            maxDate={maxDate}
            minDate={minDate}
            variant={variant}
            date={date}
        />;
    };

    const renderTimeSelector = () => {
        if(pickerType === "date") {
            return null;
        }

        return <TimeSelector
            labelPosition={pickerType === "time" && isTimeSelectWorksWithCenterWhenTimePicker ? "top" : "start"}
            isTimeSelectWorksWithCenterWhenTimePicker={isTimeSelectWorksWithCenterWhenTimePicker}
            multipleSelectMinimumRequireMS={multipleSelectMinimumRequireMS}
            multipleSelectMaximumMS={multipleSelectMaximumMS}
            isWorkWith24HoursFormat={isWorkWith24HoursFormat}
            setIsSheetContentReady={setIsSheetContentReady}
            selectMultipleObject={selectMultipleObject}
            isWorkWithSeconds={isWorkWithSeconds}
            customLocalize={customLocalize}
            selectObject={selectObject}
            customTheme={customTheme}
            pickerType={pickerType}
            dateRange={dateRange}
            maxDate={maxDate}
            minDate={minDate}
            variant={variant}
            date={date}
        />;
    };

    const renderLoading = () => {
        return <View
            style={[
                stylesheet.loadingContainer,
                loadingContainerDynamicStyle
            ]}
            aria-label={localize("loading")}
            role="progressbar"
            accessible={true}
            aria-busy={true}
        >
            {
                LoadingIconComponentProp ?
                    <LoadingIconComponentProp
                        size={iconSizes.large}
                        color="default"
                    />
                :
                    <Loading
                        customTheme={customTheme}
                        size="large"
                    />
            }
        </View>;
    };

    const renderBottomSheetContent = () => {
        if(isLoading) {
            return renderLoading();
        }

        return <View
            style={[
                stylesheet.contentContainer,
                contentContainerDynamicStyle
            ]}
        >
            {renderCalendarChanger()}
            <Animated.View
                style={isCalendarHeightAnimating ? {
                    height: calendarHeight,
                    overflow: "hidden"
                } : undefined}
            >
                <View
                    onLayout={onCalendarLayout}
                >
                    {renderCalendar()}
                </View>
            </Animated.View>
            {renderTimeSelector()}
        </View>;
    };

    const renderClearButton = () => {
        if(!isShowTools || minChoice) {
            return null;
        }

        return <Button
            title={localize("clear-selection")}
            style={stylesheet.clearButton}
            customTheme={customTheme}
            onPress={onClearPress}
            spreadBehaviour="free"
            variant="ghost"
            type="neutral"
        />;
    };

    const renderTodayButton = () => {
        if(!isShowTools || pickerType === "time") {
            return null;
        }

        return <Button
            title={localize("go-to-today")}
            customTheme={customTheme}
            onPress={onTodayPress}
            isDisabled={isLoading}
            spreadBehaviour="free"
            variant="tonal"
            type="neutral"
        />;
    };

    const renderPopoverBody = () => {
        if(isLoading) {
            return renderLoading();
        }

        return <View
            style={[
                stylesheet.contentContainer,
                contentContainerDynamicStyle
            ]}
        >
            {renderCalendar()}
            {renderTimeSelector()}
        </View>;
    };

    const renderPopover = () => {
        if(!anchorRef) {
            return null;
        }

        return <PopoverContext.Provider
            value={popoverContextValue}
        >
            <Popover.Content
                aria-label={title ?? bottomSheetProps?.title ?? localize(titleKey)}
                style={isTwoMonths || (isDayGridShown && isShowWeekNumber) ? {
                    width: popoverWidth
                } : undefined}
                customLocalize={customLocalize}
                customTheme={customTheme}
                isLoading={isLoading}
                isShowClose={false}
            >
                {renderPopoverBody()}
                <Popover.Footer
                    customTheme={customTheme}
                    isDivided={true}
                >
                    {renderClearButton()}
                    {renderTodayButton()}
                    <Button
                        customTheme={customTheme}
                        title={localize("done")}
                        spreadBehaviour="free"
                        onPress={onOkPress}
                        variant="filled"
                        type="primary"
                    />
                </Popover.Footer>
            </Popover.Content>
        </PopoverContext.Provider>;
    };

    const renderBottomSheetBottom = () => {
        return <View
            style={[
                stylesheet.footerContainer,
                footerContainerDynamicStyle
            ]}
        >
            {renderClearButton()}
            {renderTodayButton()}
            <Button
                customTheme={customTheme}
                title={localize("done")}
                spreadBehaviour="free"
                onPress={onOkPress}
                variant="filled"
                type="primary"
            />
        </View>;
    };

    if(isPopover) {
        return renderPopover();
    }

    return <BottomSheet
        {...bottomSheetProps}
        description={description ?? bottomSheetProps?.description ?? summary}
        customLocalize={customLocalize ?? bottomSheetProps?.customLocalize}
        title={title ?? bottomSheetProps?.title ?? localize(titleKey)}
        customTheme={customTheme ?? bottomSheetProps?.customTheme}
        isContentReady={isLoading || isSheetContentReady}
        key={`${dateTimePickerKey}-bottomsheet`}
        renderBottom={renderBottomSheetBottom}
        onOpenChange={handleSheetOpenChange}
        customKey={dateTimePickerKey}
        ref={bottomSheetInnerRef}
        onClosed={onClosed}
        isAutoHeight={true}
        isOpen={isOpen}
    >
        {renderBottomSheetContent()}
    </BottomSheet>;
};
export default DateTimeSheet;
